import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import {
  Search, MapPin, Calendar, ExternalLink,
  GraduationCap, Clock, CheckCircle, XCircle, MessageSquare
} from 'lucide-react';
import './DiscoverOpportunities.css';

const DiscoverOpportunities = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    message: '',
    onConfirm: null
  });

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4000);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/opportunities');
      setOpportunities(res.data);
    } catch (err) {
      console.error("Error fetching opportunities", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpressInterest = async (oppId) => {
    setConfirmModal({
      show: true,
      message: "Do you want to express interest in hosting this session?",
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await api.post(`/opportunities/${oppId}/express-interest`);
          showToast("Interest sent! The university will review your profile.", "success");
          fetchData();
        } catch (err) {
          showToast(err.response?.data?.detail || "Failed to express interest.", "error");
        }
      }
    });
  };

  if (loading) return <div className="pop-loading"><span>Searching for opportunities...</span></div>;

  return (
    <div className="pop-tab-view pop-fade-in">
      <div className="pop-card">
        <h3 className="pop-section-title">
          <Search size={22} style={{ marginRight: '8px' }} /> Discover Opportunities
        </h3>
        <p className="pop-text-muted">Browse requirements and find sessions that fit your schedule.</p>
      </div>

      <div className="pop-opp-grid">
        {opportunities.length > 0 ? opportunities.map((opp) => {

          // --- LOGIC STATES ---
          const isFilledByOther = opp.is_filled;
          const isAppliedByMe = opp.is_applied;
          const hasIDeclined = opp.application_status === 'DECLINED';

          return (
            <div key={opp.id} className="pop-card pop-opp-card">
              <div className="pop-opp-header">
                <div className="pop-uni-info">
                  <GraduationCap size={16} color="var(--pop-primary)" />
                  <span>{opp.university_name}</span>
                </div>
                <span className="pop-status-badge pop-badge-accepted">
                  {opp.engagement_type}
                </span>
              </div>

              <h4 className="pop-opp-title">{opp.title}</h4>

              <div className="pop-schedule-box">
                <div className="pop-meta-item">
                  <Calendar size={14} className="text-primary" />
                  <span>
                    {opp.event_date ? new Date(opp.event_date).toLocaleDateString('en-GB') : 'Flexible Date'}
                    {opp.end_date && ` - ${new Date(opp.end_date).toLocaleDateString('en-GB')}`}
                  </span>
                </div>
                <div className="pop-meta-item">
                  <Clock size={14} className="text-primary" />
                  <span>
                    {opp.start_time || 'TBD'}
                    {opp.end_time ? ` - ${opp.end_time}` : ''}
                    {!!opp.duration_value && ` (${opp.duration_value} ${opp.duration_unit})`}
                  </span>
                </div>
                {opp.days_of_week && opp.days_of_week.length > 0 && (
                  <div className="pop-meta-item" style={{ color: 'var(--pop-primary)', fontWeight: 600 }}>
                    <Calendar size={14} />
                    <span>{opp.days_of_week.join(', ')}</span>
                  </div>
                )}
                {!!opp.weekly_hours && (
                  <div className="pop-meta-item" style={{ color: '#0369a1' }}>
                    <Clock size={14} />
                    <span>{opp.weekly_hours}h/week Quota</span>
                  </div>
                )}
              </div>

              <div className="pop-opp-meta">
                <div className="pop-meta-item">
                  <MapPin size={14} />
                  <span>
                    {opp.delivery_mode}
                    {(opp.delivery_mode === 'In-Person' || opp.delivery_mode === 'Hybrid') && opp.location ? ` (${opp.location})` : ''}
                  </span>
                </div>
                <div className="pop-meta-item">
                  <span className="domain-tag">{opp.required_domain}</span>
                </div>
                <div className="pop-meta-item" style={{ marginLeft: 'auto' }}>
                  <span className="rate-badge">₹{opp.total_estimated_amount || 0} <span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 500 }}>({opp.university_tier})</span></span>
                </div>
              </div>

              {/* DYNAMIC BUTTON LOGIC */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  className={`pop-btn-primary ${(isFilledByOther || isAppliedByMe || hasIDeclined) ? "disabled-btn" : ""}`}
                  disabled={isFilledByOther || isAppliedByMe || hasIDeclined}
                  style={{
                    flex: 2,
                    justifyContent: 'center',
                    display: 'flex',
                    gap: '4px',
                    backgroundColor: hasIDeclined ? '#ef4444' : isFilledByOther ? 'var(--text-faint)' : isAppliedByMe ? '#6366f1' : ''
                  }}
                  onClick={() => handleExpressInterest(opp.id)}
                >
                  {isFilledByOther ? (
                    <>Requirement Satisfied <CheckCircle size={16} /></>
                  ) : hasIDeclined ? (
                    <>Declined <XCircle size={16} /></>
                  ) : isAppliedByMe ? (
                    <>Pending Review <Clock size={16} /></>
                  ) : (
                    <>Interest <ExternalLink size={16} /></>
                  )}
                </button>

                <button
                  className="pop-btn-outline"
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    borderColor: 'var(--border)'
                  }}
                  onClick={() => navigate('/chat', {
                    state: {
                      target_user_id: opp.university_id,
                      target_name: opp.university_name
                    }
                  })}
                  title="Message University"
                >
                  <MessageSquare size={18} />
                </button>
              </div>
            </div>
          );
        }) : (
          <div className="pop-card" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px' }}>
            <p className="pop-text-muted">
              No opportunities available at the moment.
              <br />
              <span style={{ fontSize: '13px', marginTop: '8px', display: 'block' }}>
                Note: You must be approved through vetting to view university opportunities.
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'modalSlideIn 0.3s ease-out'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '32px'
              }}>
                ⚠️
              </div>
              <h2 style={{
                margin: '0 0 8px',
                fontSize: '24px',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Confirm Action
              </h2>
              <p style={{
                margin: 0,
                color: '#6b7280',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>
                {confirmModal.message}
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '2px solid var(--border)',
                  background: 'var(--graphite-dark)',
                  color: 'var(--text-muted)',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.background = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => confirmModal.onConfirm()}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#059669'}
                onMouseLeave={(e) => e.target.style.background = '#10b981'}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              background: toast.type === 'success' ? '#10b981' : '#ef4444',
              color: '#fff',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: '300px',
              maxWidth: '500px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideIn 0.3s ease-out',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <span style={{ fontSize: '20px' }}>
              {toast.type === 'success' ? '\u2713' : '\u2715'}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.8'}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes modalSlideIn {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default DiscoverOpportunities;
